import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import type {DragDirection, Span} from '@Libraries/timeline/types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {OverlappingSubtitleItemResizeData} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';

export const isOverlapping = (updatedRange: Span, startTime: number, endTime: number): boolean => {
  return updatedRange.start < endTime && updatedRange.end > startTime;
};

export const areSubtitlesOverlappingWithThisRange = (updatedRange: Span, idOftemBeingEdited: string, transcriptObject: TranscriptItemObject): boolean => {
  for (const [key, item] of Object.entries(transcriptObject.subtitlesHashmap)) {
    if (key !== idOftemBeingEdited && isOverlapping(updatedRange, item.startTime, item.endTime)) {
      return true;
    }
  }
  return false;
};

export const getOverlapAndSubtitleIds = (
  updatedRange: Span,
  idOftemBeingEdited: string,
  direction: DragDirection,
  transcriptObject: TranscriptItemObject
): OverlappingSubtitleItemResizeData | null => {
  const transcriptItem = window.posterEditor?.whiteboard?.getCurrentPage().items.getItem(transcriptObject.uid) as TranscriptItem;

  if (!transcriptItem) {
    return null;
  }

  const sortedIds: [string, number, number][] = [];
  for (const [key, item] of Object.entries(transcriptItem.subtitlesHashmap)) {
    sortedIds.push([key, item.startTime, item.endTime]);
  }

  if (direction === 'end') {
    sortedIds.sort((a, b) => {
      return a[1] - b[1];
    });
    const startIndex = sortedIds.findIndex((row) => {
      return row[0] === idOftemBeingEdited;
    });
    if (startIndex === sortedIds.length - 1) {
      return null;
    }

    const overlap = updatedRange.end - sortedIds[startIndex + 1][1];
    if (overlap < 0) {
      return null;
    }

    return {
      overlap,
      ids: sortedIds.slice(startIndex + 1).map((subArray) => {
        return subArray[0];
      }),
      direction,
    };
  }
  sortedIds.sort((a, b) => {
    return a[2] - b[2];
  });
  const startIndex = sortedIds.findIndex((row) => {
    return row[0] === idOftemBeingEdited;
  });
  if (startIndex === 0) {
    return null;
  }
  const overlap = sortedIds[startIndex - 1][2] - updatedRange.start;
  if (sortedIds[0][1] - overlap < 0) {
    return null;
  }

  if (overlap < 0) {
    return null;
  }

  return {
    overlap: overlap * -1,
    ids: sortedIds.slice(0, startIndex).map((subArray) => {
      return subArray[0];
    }),
    direction,
  };
};

export const canItemBeMoveToThisRange = (range: Span, direction: DragDirection, idOftemBeingEdited: string, transcriptItemId: string): boolean => {
  const transcriptItem = window.posterEditor?.whiteboard?.getCurrentPage().items.getItem(transcriptItemId) as TranscriptItem;
  const pageDuration = window.posterEditor?.whiteboard?.getCurrentPage().getDuration();
  if (!transcriptItem) {
    throw new Error('unable to find the transcript item');
  }
  if (!pageDuration) {
    return false;
  }

  const actualDuration = transcriptItem.subtitlesHashmap[idOftemBeingEdited].endTime - transcriptItem.subtitlesHashmap[idOftemBeingEdited].startTime;
  const resizeDuration = range.end - range.start;
  const difference = resizeDuration - actualDuration;

  if (difference < 0) {
    return true;
  }

  if (!areSubtitlesOverlappingWithThisRange(range, idOftemBeingEdited, transcriptItem.toObject())) {
    return true;
  }

  const sortedIds: [string, number, number][] = [];
  for (const [key, item] of Object.entries(transcriptItem.subtitlesHashmap)) {
    sortedIds.push([key, item.startTime, item.endTime]);
  }

  if (direction === 'end') {
    sortedIds.sort((a, b) => {
      return a[1] - b[1];
    });
    const startIndex = sortedIds.findIndex((row) => {
      return row[0] === idOftemBeingEdited;
    });
    if (startIndex === sortedIds.length - 1) {
      return true;
    }

    const overlap = range.end - sortedIds[startIndex + 1][1];
    if (sortedIds[sortedIds.length - 1][2] + overlap > pageDuration) {
      return false;
    }
  } else {
    sortedIds.sort((a, b) => {
      return a[2] - b[2];
    });
    const startIndex = sortedIds.findIndex((row) => {
      return row[0] === idOftemBeingEdited;
    });
    const overlap = sortedIds[startIndex - 1][2] - range.start;
    if (sortedIds[0][1] - overlap < 0) {
      return false;
    }
  }

  return true;
};
